import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sprout, Plus, Calendar, Clock, Trash2, Pencil, X,
  AlertTriangle, CheckCircle2, Timer, Leaf, BarChart3,
  Sparkles, TrendingUp, Layers, MapPin, ChevronDown
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const statusConfig = {
  seeded:  { label: "Seeded", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", icon: Sprout },
  growing: { label: "Growing", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", icon: Leaf },
  ready:   { label: "Ready to Harvest", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", icon: CheckCircle2 },
  overdue: { label: "Harvest Overdue", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: AlertTriangle },
  harvested: { label: "Harvested", color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200", icon: CheckCircle2 },
  failed:  { label: "Failed", color: "text-red-400", bg: "bg-red-50/50", border: "border-red-100", icon: X },
};

const MICROGREEN_TYPES = [
  "Radish Red", "Radish Microgreens", "Basil Microgreens", "Broccoli Microgreens",
  "Sunflower Microgreens", "Pea Shoots", "Wheatgrass", "Amaranthus Microgreens",
  "Mustard Microgreens", "Fenugreek Microgreens", "Cilantro Microgreens",
  "Kale Microgreens", "Cabbage Microgreens", "Beets Microgreens",
  "Kohlrabi Microgreens", "Spinach Microgreens", "Arugula Microgreens",
  "Lentil Microgreens", "Mung Bean Microgreens", "Coriander Microgreens"
];

const emptyBatch = {
  microgreen_type: "", batch_id: "", tray_count: 1,
  sowing_date: new Date().toISOString().slice(0, 10), sowing_time: "08:00",
  rack_location: "", notes: ""
};

function getProgress(sowingDate, harvestDate) {
  const now = new Date();
  const sow = new Date(sowingDate);
  const harv = new Date(harvestDate);
  const total = harv - sow;
  if (total <= 0) return 100;
  const elapsed = now - sow;
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

function getDaysLeft(harvestDate) {
  const now = new Date();
  const harv = new Date(harvestDate);
  const diff = Math.ceil((harv - now) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function AdminHarvest() {
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBatch);
  const [notifDismissed, setNotifDismissed] = useState(false);
  const { getToken } = useAuth();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/harvest/analytics`, { headers });
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchAI = async () => {
    setAiLoading(true);
    try {
      const res = await axios.get(`${API}/admin/harvest/ai-recommendations`, { headers });
      setRecommendations(res.data);
    } catch (e) { toast.error("AI recommendation failed"); }
    finally { setAiLoading(false); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyBatch); setDialogOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm(b); setDialogOpen(true); };
  const updateForm = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  const handleSave = async () => {
    if (!form.microgreen_type || !form.sowing_date) { toast.error("Fill required fields"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/admin/harvest/batches/${editing}`, form, { headers });
        toast.success("Batch updated");
      } else {
        await axios.post(`${API}/admin/harvest/batches`, form, { headers });
        toast.success("Batch created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this batch?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/batches/${id}`, { headers });
      toast.success("Batch deleted");
      fetchData();
    } catch (e) { toast.error("Delete failed"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/harvest/batches/${id}`, { status }, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (e) { toast.error("Update failed"); }
  };

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-64 bg-gray-200 animate-pulse rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-xl" />)}
      </div>
    </div>
  );

  const { all_batches = [], due_today_batches = [], overdue_batches = [] } = analytics || {};
  const activeBatches = all_batches.filter(b => !["harvested", "failed"].includes(b.status));
  const notifications = [...due_today_batches, ...overdue_batches];

  return (
    <div className="space-y-6" data-testid="admin-harvest">
      {/* Notifications */}
      <AnimatePresence>
        {!notifDismissed && notifications.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <Card className="border-amber-200 bg-amber-50/80" data-testid="harvest-notification">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 text-sm">Harvest Attention Required</p>
                  {notifications.map(b => (
                    <p key={b.id} className="text-sm text-amber-700 mt-1">
                      <span className="font-medium">{b.batch_id}</span> ({b.microgreen_type}) — {b.status === "overdue" ? "overdue for harvest!" : "ready to harvest today!"}
                    </p>
                  ))}
                </div>
                <button onClick={() => setNotifDismissed(true)} className="text-amber-400 hover:text-amber-600"><X className="w-4 h-4" /></button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Smart Harvest Tracker</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track crop cycles, monitor growth & get AI insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAI} disabled={aiLoading} className="text-sm" data-testid="ai-suggest-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> {aiLoading ? "Analyzing..." : "AI Suggest"}
          </Button>
          <Button onClick={openCreate} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="add-batch-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Batch
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Harvest Due Today", value: analytics?.due_today || 0, icon: Calendar, color: "bg-amber-500" },
          { label: "Active Batches", value: analytics?.active_batches || 0, icon: Sprout, color: "bg-emerald-500" },
          { label: "Trays Growing", value: analytics?.total_trays_growing || 0, icon: Layers, color: "bg-blue-500" },
          { label: "Upcoming (7 days)", value: analytics?.upcoming_week || 0, icon: Timer, color: "bg-violet-500" },
        ].map(s => (
          <Card key={s.label} className="border-gray-200" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`${s.color} w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Batch Timeline — spans 2 cols */}
        <div className="lg:col-span-2 space-y-3">
          <h2 className="font-heading text-lg font-semibold text-gray-900">Active Batches</h2>
          {activeBatches.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-8 text-center text-gray-400">
              <Sprout className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              No active batches. Start by adding a new batch.
            </CardContent></Card>
          ) : (
            activeBatches.map((b, i) => {
              const cfg = statusConfig[b.status] || statusConfig.seeded;
              const StatusIcon = cfg.icon;
              const progress = getProgress(b.sowing_date, b.harvest_date);
              const daysLeft = getDaysLeft(b.harvest_date);

              return (
                <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className={`border ${cfg.border} overflow-hidden`} data-testid={`batch-card-${b.batch_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                          <StatusIcon className={`w-5 h-5 ${cfg.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-gray-800">{b.microgreen_type}</h3>
                            <span className="font-mono text-xs text-gray-400">{b.batch_id}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Sown: {b.sowing_date}</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Harvest: {b.harvest_date}</span>
                            <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {b.tray_count} trays</span>
                            {b.rack_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.rack_location}</span>}
                          </div>
                          {/* Progress */}
                          <div className="mt-3 flex items-center gap-3">
                            <Progress value={progress} className="flex-1 h-2" />
                            <span className="text-xs font-semibold text-gray-600 w-12 text-right">{progress}%</span>
                          </div>
                          <p className="text-xs mt-1 text-gray-400">
                            {daysLeft > 0 ? `${daysLeft} day${daysLeft > 1 ? "s" : ""} until harvest` : daysLeft === 0 ? "Harvest today!" : `${Math.abs(daysLeft)} day${Math.abs(daysLeft) > 1 ? "s" : ""} overdue`}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {(b.status === "ready" || b.status === "overdue") && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-full h-7 px-3"
                              onClick={() => updateStatus(b.id, "harvested")} data-testid={`harvest-${b.batch_id}`}>
                              Harvest
                            </Button>
                          )}
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(b)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDelete(b.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>

        {/* AI Recommendations — right panel */}
        <div className="space-y-3">
          <h2 className="font-heading text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" /> AI Crop Suggestions
          </h2>
          {!recommendations && !aiLoading ? (
            <Card className="border-gray-200 border-dashed">
              <CardContent className="p-6 text-center">
                <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400 mb-3">Get AI-powered crop recommendations</p>
                <Button variant="outline" onClick={fetchAI} className="text-sm rounded-full" data-testid="ai-suggest-empty">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate Suggestions
                </Button>
              </CardContent>
            </Card>
          ) : aiLoading ? (
            <Card className="border-gray-200"><CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-praroha-sage border-t-transparent mx-auto mb-2" />
              <p className="text-sm text-gray-500">Analyzing sales & seasonality...</p>
            </CardContent></Card>
          ) : (
            <>
              <p className="text-xs text-gray-400">Season: {recommendations?.season} | {recommendations?.month}</p>
              {recommendations?.recommendations?.map((rec, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}>
                  <Card className={`border ${rec.priority === "high" ? "border-emerald-200 bg-emerald-50/30" : "border-gray-200"}`} data-testid={`ai-rec-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="font-heading font-semibold text-gray-800 text-sm">{rec.crop}</h4>
                          <p className="text-xs text-gray-500 mt-1">{rec.reason}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${
                          rec.priority === "high" ? "bg-emerald-100 text-emerald-700" :
                          rec.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
                        }`}>{rec.priority}</span>
                      </div>
                      <div className="flex gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {rec.suggested_trays} trays</span>
                        <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {rec.estimated_demand}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
              <Button variant="ghost" onClick={fetchAI} className="w-full text-xs text-gray-400 hover:text-gray-600" data-testid="ai-refresh">
                <Sparkles className="w-3 h-3 mr-1" /> Refresh suggestions
              </Button>
            </>
          )}

          {/* Smart Insights */}
          <h2 className="font-heading text-lg font-semibold text-gray-900 flex items-center gap-2 pt-2">
            <BarChart3 className="w-4 h-4 text-blue-500" /> Quick Insights
          </h2>
          <Card className="border-gray-200">
            <CardContent className="p-4 space-y-3">
              {[
                { label: "Harvests this week", value: analytics?.upcoming_week || 0 },
                { label: "Trays growing", value: analytics?.total_trays_growing || 0 },
                { label: "Overdue batches", value: analytics?.overdue_count || 0 },
                { label: "Active batches", value: analytics?.active_batches || 0 },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{s.label}</span>
                  <span className="font-heading font-bold text-gray-900">{s.value}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Batch" : "New Harvest Batch"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Microgreen Type *</Label>
              <Select value={form.microgreen_type} onValueChange={v => updateForm("microgreen_type", v)}>
                <SelectTrigger data-testid="batch-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {MICROGREEN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sowing Date *</Label>
                <Input type="date" value={form.sowing_date} onChange={e => updateForm("sowing_date", e.target.value)} data-testid="batch-sowing-date" />
              </div>
              <div>
                <Label>Sowing Time</Label>
                <Input type="time" value={form.sowing_time} onChange={e => updateForm("sowing_time", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tray Count</Label>
                <Input type="number" min={1} value={form.tray_count} onChange={e => updateForm("tray_count", parseInt(e.target.value) || 1)} data-testid="batch-tray-count" />
              </div>
              <div>
                <Label>Rack Location</Label>
                <Input value={form.rack_location} onChange={e => updateForm("rack_location", e.target.value)} placeholder="e.g. Rack A-3" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => updateForm("notes", e.target.value)} rows={2} placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-praroha-forest hover:bg-praroha-forest/90" data-testid="save-batch-btn">Save Batch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
